import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, QuestionIcon } from '@chakra-ui/icons';
import { DEAL_TYPE } from 'constants/dealConstants';
import { PercentageNumberInput, TwNumberInput } from 'components/NumberInput';
import { renderInputElement } from 'helpers/formHelper';
import { formatRequiredFieldLabel } from 'helpers/requiredFieldLabel';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import RoasVectorEditor from '../RoasVectorEditor';
import InstallmentAmortizationSettings from './InstallmentAmortizationSettings';

const fieldsRowDates = [
  'cohortStartDate',
  'firstRepaymentDate',
];

const fieldsRowSchedule = [
  'repaymentFrequency',
  'repaymentDays',
  'daysBeforeEndOfMonth',
  'numberOfRepayments',
];

const fieldsRowFee = [
  'feeSettings.feeType',
  'feeSettings.feePct',
  'feeSettings.irrCapPct',
];

const fieldsRowRevenue = ['revenueTotalType', 'pctFromRevenueShare'];

const trueUpFeeCollectionAndAccelerationToggleFields = [
  'isTrueUp',
  'feeCollectionAtLastPayment',
  'accelerationEnabled',
];

const FieldsRow = ({
  control,
  allFields,
  fieldNames,
  isDisabled,
  onFieldChange,
}) => {
  return (
    <Flex gap={4}>
      {fieldNames.map((name) => {
        const fieldItem = allFields.find((f) => f.name === name);

        if (!fieldItem) {
          return null;
        }

        if (fieldItem.isHidden) {
          return null;
        }

        const isHorizontal = fieldItem.element === Checkbox;

        return (
          <Flex
            key={name}
            direction={'column'}
            justifyContent={'space-between'}
            w={name === 'feeSettings.irrCapPct' ? 'auto' : 220}
            minW={name === 'feeSettings.irrCapPct' ? '260px' : undefined}
          >
            <Flex
              align="center"
              gap={1}
              visibility={isHorizontal ? 'hidden' : 'visible'}
            >
              <FormLabel
                fontSize={'sm'}
                whiteSpace={'nowrap'}
                mb={0}
              >
                {formatRequiredFieldLabel(fieldItem.label, true)}
              </FormLabel>
              {fieldItem.tooltip && (
                <Tooltip label={fieldItem.tooltip} fontSize="xs" maxW="300px">
                  <QuestionIcon boxSize={3} color="gray.500" cursor="help" />
                </Tooltip>
              )}
            </Flex>
            <Flex align={'center'}>
              {isHorizontal && (
                <Flex align="center" gap={1}>
                  <FormLabel fontSize={'sm'} whiteSpace={'nowrap'} mb={0}>
                    {formatRequiredFieldLabel(fieldItem.label, true)}
                  </FormLabel>
                  {fieldItem.tooltip && (
                    <Tooltip label={fieldItem.tooltip} fontSize="xs" maxW="300px">
                      <QuestionIcon boxSize={3} color="gray.500" cursor="help" />
                    </Tooltip>
                  )}
                </Flex>
              )}
              <Box w={'full'}>
                <Controller
                  control={control}
                  name={name}
                  render={renderInputElement({
                    item: fieldItem,
                    styleProps: { fontWeight: 800 },
                    showPrefix: true,
                    isDisabled: isDisabled,
                    onChange: (name, value) => {
                      onFieldChange(name, value);
                    },
                  })}
                />
              </Box>
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};

const accelerationDetailFieldNames = [
  'accelerationPct',
  'accelerationRevenueTotalType',
];

const FieldsColumn = ({
  control,
  allFields,
  fieldNames,
  isDisabled,
  onFieldChange,
}) => {
  return (
    <VStack spacing={3} align="left">
      {fieldNames.map((name) => {
        const fieldItem = allFields.find((f) => f.name === name);

        if (!fieldItem || fieldItem.isHidden) {
          return null;
        }

        return (
          <Flex key={name} align="center" gap={2} w={320}>
            <Flex align="center" gap={1} flex="1">
              <FormLabel fontSize={'sm'} whiteSpace={'nowrap'} mb={0}>
                {formatRequiredFieldLabel(fieldItem.label, false)}
              </FormLabel>
              {fieldItem.tooltip && (
                <Tooltip label={fieldItem.tooltip} fontSize="xs" maxW="300px">
                  <QuestionIcon boxSize={3} color="gray.500" cursor="help" />
                </Tooltip>
              )}
            </Flex>
            <Box>
              <Controller
                control={control}
                name={name}
                render={renderInputElement({
                  item: fieldItem,
                  styleProps: { fontWeight: 800 },
                  showPrefix: true,
                  isDisabled: isDisabled,
                  onChange: (name, value) => {
                    onFieldChange(name, value);
                  },
                })}
              />
            </Box>
          </Flex>
        );
      })}
    </VStack>
  );
};

const AccelerationSection = ({ control, allFields, isDisabled, onFieldChange }) => {
  return (
    <Box borderLeft="3px solid" borderColor="purple.500" pl={4}>
      <Text fontWeight="semibold" fontSize="sm" color="purple.600" mb={3}>
        Acceleration
      </Text>
      <Box pb={4}>
        <FieldsRow
          control={control}
          allFields={allFields}
          fieldNames={accelerationDetailFieldNames}
          isDisabled={isDisabled}
          onFieldChange={onFieldChange}
        />
      </Box>
    </Box>
  );
};

const RoasThresholdsSection = ({ roasThresholds, setRoasThresholds, isDisabled }) => {
  return (
    <Box borderLeft="3px solid" borderColor="orange.500" pl={4}>
      <Flex align="center" gap={1} mb={3}>
        <Text fontWeight="semibold" fontSize="sm" color="orange.600">
          ROAS Thresholds (Condition Precedent)
        </Text>
        <Tooltip
          label="Minimum cumulative ROAS % required at each 0-based MOB (month 0 = first month). Inherited from deal but can be overridden per installment."
          fontSize="xs"
          maxW="300px"
        >
          <QuestionIcon boxSize={3} color="gray.500" cursor="help" />
        </Tooltip>
      </Flex>
      <Box pb={4}>
        <Flex gap={4} mb={2} align="center">
          <Box w={120}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">Month</Text>
          </Box>
          <Box w={150}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">Min ROAS %</Text>
          </Box>
          {!isDisabled && (
            <Box w={10}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">Action</Text>
            </Box>
          )}
        </Flex>
        {[...roasThresholds]
          .sort((a, b) => {
            if (a.month === '' || a.month === null || a.month === undefined) return 1;
            if (b.month === '' || b.month === null || b.month === undefined) return -1;
            return Number(a.month) - Number(b.month);
          })
          .map((threshold) => {
            const isDuplicate =
              roasThresholds.filter(
                (t) => t.id !== threshold.id && t.month !== '' && t.month === threshold.month
              ).length > 0;

            return (
              <Flex key={threshold.id} gap={4} mb={3} align="center">
                <Box w={120}>
                  <TwNumberInput
                    value={threshold.month}
                    min={0}
                    onChange={(value) => {
                      setRoasThresholds(
                        roasThresholds.map((t) =>
                          t.id === threshold.id ? { ...t, month: value } : t
                        )
                      );
                    }}
                    inputProps={{
                      fontWeight: 800,
                      borderColor: isDuplicate ? 'red.500' : undefined,
                      _focus: isDuplicate ? { borderColor: 'red.500' } : undefined,
                    }}
                    isDisabled={isDisabled}
                  />
                  {isDuplicate && (
                    <Text fontSize="xs" color="red.500" mt={1} fontWeight="semibold">
                      Duplicate month
                    </Text>
                  )}
                </Box>
                <Box w={150}>
                  <PercentageNumberInput
                    value={threshold.minRoasPct}
                    onChange={(value) => {
                      setRoasThresholds(
                        roasThresholds.map((t) =>
                          t.id === threshold.id ? { ...t, minRoasPct: value } : t
                        )
                      );
                    }}
                    inputProps={{ fontWeight: 800 }}
                    isDisabled={isDisabled}
                  />
                </Box>
                {!isDisabled && (
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => {
                      setRoasThresholds(roasThresholds.filter((t) => t.id !== threshold.id));
                    }}
                    isDisabled={roasThresholds.length === 1}
                    aria-label="Delete threshold"
                  />
                )}
              </Flex>
            );
          })}
        {!isDisabled && (
          <Button
            leftIcon={<AddIcon />}
            size="sm"
            variant="outline"
            colorScheme="orange"
            onClick={() => {
              const newId = Math.max(...roasThresholds.map((t) => t.id)) + 1;
              setRoasThresholds([...roasThresholds, { id: newId, month: '', minRoasPct: '' }]);
            }}
          >
            Add Month
          </Button>
        )}
      </Box>
    </Box>
  );
};

const RepaymentsScheduleSettings = ({
  amortization,
  amountSold,
  onBuildAmortization,
  onAmortizationChange,
  fields,
  onFieldChange,
  control,
  isPageDisabled,
  isAmortizationBuildDisabled,
  dealType,
  feeType,
  calcAmortizationRepaymentsSum,
  isTrueUp,
  accelerationEnabled,
  accelerationPct,
  roasThresholds,
  setRoasThresholds,
  roasVector,
  setRoasVector,
}) => {
  if (!fields || fields.length === 0) {
    return null;
  }

  const hasAmortization = amortization && amortization.length > 0;
  const isFieldsDisabled = isPageDisabled || hasAmortization;
  const isAccelerationEnabled = accelerationEnabled === true;

  return (
    <Box>
      <Heading fontSize={16} mb={5}>
        Repayment Schedule
      </Heading>
      <VStack spacing={4} align={'left'}>
        <FieldsRow
          control={control}
          allFields={fields}
          fieldNames={
            dealType === DEAL_TYPE.ROAS
              ? fieldsRowDates
              : ['firstRepaymentDate']
          }
          isDisabled={isFieldsDisabled}
          onFieldChange={onFieldChange}
        />
        <FieldsRow
          control={control}
          allFields={fields}
          fieldNames={fieldsRowSchedule}
          isDisabled={isFieldsDisabled}
          onFieldChange={onFieldChange}
        />
        <FieldsRow
          control={control}
          allFields={fields}
          fieldNames={fieldsRowFee}
          isDisabled={isFieldsDisabled}
          onFieldChange={onFieldChange}
        />
        {dealType === DEAL_TYPE.ROAS && (
          <>
            <FieldsRow
              control={control}
              allFields={fields}
              fieldNames={fieldsRowRevenue}
              isDisabled={isFieldsDisabled}
              onFieldChange={onFieldChange}
            />
            <FieldsColumn
              control={control}
              allFields={fields}
              fieldNames={trueUpFeeCollectionAndAccelerationToggleFields}
              isDisabled={isFieldsDisabled}
              onFieldChange={onFieldChange}
            />
            <FieldsRow
              control={control}
              allFields={fields}
              fieldNames={['roasCovenantRevenueTotalType', 'roasCovenantMultiplier']}
              isDisabled={isFieldsDisabled}
              onFieldChange={onFieldChange}
            />
            {isAccelerationEnabled && (
              <>
                <AccelerationSection
                  control={control}
                  allFields={fields}
                  isDisabled={isFieldsDisabled}
                  onFieldChange={onFieldChange}
                />
                <RoasThresholdsSection
                  roasThresholds={roasThresholds}
                  setRoasThresholds={setRoasThresholds}
                  isDisabled={isFieldsDisabled}
                />
              </>
            )}
          </>
        )}
        <RoasVectorEditor
          points={roasVector}
          onChange={setRoasVector}
          isDisabled={isFieldsDisabled}
        />
        <Flex>
          <Button
            variant={'outline'}
            onClick={onBuildAmortization}
            mt={4}
            isDisabled={isAmortizationBuildDisabled || hasAmortization}
          >
            Build Schedule
          </Button>
        </Flex>
      </VStack>
      <Box mt={10}>
        <InstallmentAmortizationSettings
          repayments={amortization}
          amountSold={amountSold}
          onChange={onAmortizationChange}
          dealType={dealType}
          feeType={feeType}
          calcAmortizationRepaymentsSum={calcAmortizationRepaymentsSum}
          isTrueUp={isTrueUp}
          accelerationPct={accelerationPct}
        />
      </Box>
    </Box>
  );
};

RepaymentsScheduleSettings.propTypes = {
  amortizationRepayments: PropTypes.array,
  amountSold: PropTypes.number,
  fields: PropTypes.array,
  onFieldChange: PropTypes.func,
  control: PropTypes.object,
  isPageDisabled: PropTypes.bool,
  onAmortizationChange: PropTypes.func,
  onBuildAmortization: PropTypes.func,
  isAmortizationBuildDisabled: PropTypes.bool,
  dealType: PropTypes.string,
  calcAmortizationRepaymentsSum: PropTypes.func,
  isTrueUp: PropTypes.bool,
  accelerationEnabled: PropTypes.bool,
  accelerationPct: PropTypes.number,
  roasThresholds: PropTypes.array,
  setRoasThresholds: PropTypes.func,
  roasVector: PropTypes.array,
  setRoasVector: PropTypes.func,
};

export default RepaymentsScheduleSettings;
